import PropTypes from 'prop-types';

import {Box, Container as BaseContainer, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component as ContentfulComponent} from '@amblique/king-living/app/components/contentful/component';

import {getContainerFullWidthChakraProps, getContainerFullWidthClassName} from '@amblique/king-living/app/contentful/utils/container-full-width-chakra-props';
import {normalizeContainerFullWidth} from '@amblique/king-living/app/contentful/utils/normalize-container-full-width';

/**
 * Optional full-width wrapper when `backgroundColour` is set. That outer band uses
 * `className="component-container__background"` (Chakra still emits a hash class for Emotion styles).
 *
 * @param {Object} props
 * @param {Contentful.Container} props.component
 * @param {boolean} [props.isComplete]
 * @param {boolean} [props.isFetching]
 */
export const Container = ({component, isComplete = true, isFetching = false, className, ...props}) => {
  const styles = useMultiStyleConfig('contentful/container', {component, isComplete, isFetching});
  const fullWidthMode = normalizeContainerFullWidth(component?.fullWidth);
  const fullWidthChakraProps = getContainerFullWidthChakraProps(fullWidthMode);
  const fullWidthClassName = getContainerFullWidthClassName(fullWidthMode);

  const hasBackgroundWrapper = Boolean(styles.wrapper?.backgroundColor);

  // `fullWidth: Yes` — render `Box` instead of Chakra `<Container>`. The shared `Container`
  // theme applies responsive `paddingLeft` / `paddingRight`; `px={0}` does not always win the
  // style merge, so `chakra-container` still showed horizontal padding in DevTools.
  const layoutProps = {
    ...styles.root,
    ...props,
    ...fullWidthChakraProps,
    className: [className, fullWidthClassName].filter(Boolean).join(' '),
  };

  // Contentful's GraphQL Delivery API returns `null` placeholders inside
  // collection arrays when a referenced entry is unpublished, archived, or
  // otherwise unavailable. Filter them out before dereferencing `child.sys.id`
  // (otherwise the whole page crashes with `Cannot read properties of null
  // (reading 'sys')`).
  const region = (component.region1Collection?.items ?? [])
    .filter(Boolean)
    .map((child) => (
      <ContentfulComponent key={`component[${child.sys.id}]`} component={child} />
    ));

  const content =
    fullWidthMode === 'Yes' ?
      <Box {...layoutProps}>{region}</Box> :
      <BaseContainer {...layoutProps}>{region}</BaseContainer>;

  if (hasBackgroundWrapper) {
    return (
      <Box {...styles.wrapper} className="component-container__background">
        {content}
      </Box>
    );
  }

  return content;
};

Container.displayName = 'Container';
Container.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
